import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 8
const MEMORY_SIZE = 0x0400
const ACTIVE_START = 0x02d0
const SCAN_START = 0x02d4
const TONE_DCS_N = 8000
const TONE_DCS_R = 12000

type M60Spec = {
  id: string
  model: string
  type: string
  summary: string
  channels: 2 | 32
}

export type KenwoodM60Profile = {
  protocol: 'kenwood-m60'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  memorySize: number
  types: readonly string[]
  powerLevels: readonly ['Low', 'High']
}

const SPECS: readonly M60Spec[] = [
  { id: 'kenwood-tk-760', model: 'TK-760', type: 'M0760', summary: 'Kenwood TK-760 VHF mobile.', channels: 32 },
  { id: 'kenwood-tk-762', model: 'TK-762', type: 'M0762', summary: 'Kenwood TK-762 compact VHF mobile.', channels: 2 },
  { id: 'kenwood-tk-768', model: 'TK-768', type: 'M0768', summary: 'Kenwood TK-768 VHF mobile.', channels: 32 },
  { id: 'kenwood-tk-860', model: 'TK-860', type: 'M0860', summary: 'Kenwood TK-860 UHF mobile.', channels: 32 },
  { id: 'kenwood-tk-862', model: 'TK-862', type: 'M0862', summary: 'Kenwood TK-862 compact UHF mobile.', channels: 2 },
  { id: 'kenwood-tk-868', model: 'TK-868', type: 'M0868', summary: 'Kenwood TK-868 UHF mobile.', channels: 32 },
]

export const KENWOOD_M60_PROFILES: readonly KenwoodM60Profile[] = SPECS.map(
  (spec) => ({
    protocol: 'kenwood-m60',
    id: spec.id,
    vendor: 'Kenwood',
    model: spec.model,
    summary: spec.summary,
    tags: ['Experimental', 'Read + write', `${spec.channels} channels`, 'No channel names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: spec.channels,
    nameLength: 0,
    memorySize: MEMORY_SIZE,
    types: [spec.type],
    powerLevels: ['Low', 'High'],
  }),
)

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function active(image: Uint8Array, number: number): boolean {
  const index = number - 1
  return (image[ACTIVE_START + Math.floor(index / 8)] & (1 << (index & 7))) !== 0
}

function setActive(image: Uint8Array, number: number, value: boolean): void {
  const index = number - 1
  const offset = ACTIVE_START + Math.floor(index / 8)
  const bit = 1 << (index & 7)
  image[offset] = value ? image[offset] | bit : image[offset] & ~bit
}

function scan(image: Uint8Array, number: number): boolean {
  const index = number - 1
  return (image[SCAN_START + Math.floor(index / 8)] & (1 << (index & 7))) !== 0
}

function setScan(image: Uint8Array, number: number, value: boolean): void {
  const index = number - 1
  const offset = SCAN_START + Math.floor(index / 8)
  const bit = 1 << (index & 7)
  image[offset] = value ? image[offset] | bit : image[offset] & ~bit
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function rawBcdValue(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    value = value * 100 + (raw[index] >> 4) * 10 + (raw[index] & 0x0f)
  }
  return value
}

function decimalBcd(value: number): Uint8Array {
  const result = new Uint8Array(2)
  for (let index = 0; index < 2; index += 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = rawBcdValue(raw)
  if (value === null || value === 0 || value === 16665) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= TONE_DCS_R) {
    return { mode: 'dtcs', ctcss: null, dtcs: value - TONE_DCS_R, polarity: 'R' }
  }
  if (value >= TONE_DCS_N) {
    return { mode: 'dtcs', ctcss: null, dtcs: value - TONE_DCS_N, polarity: 'N' }
  }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): Uint8Array {
  if (channel.toneMode === 'none') return Uint8Array.of(0xff, 0xff)
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    return decimalBcd(Math.round(channel.ctcss * 10))
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error('That DCS code is not supported by this radio.')
    }
    const result = decimalBcd(channel.dtcs)
    result[1] |= channel.dtcsPolarity === 'R' ? 0xc0 : 0x80
    return result
  }
  return Uint8Array.of(0xff, 0xff)
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return tone.mode === 'dtcs' && tone.dtcs === channel.dtcs && tone.polarity === channel.dtcsPolarity
}

export function parseKenwoodM60Channel(
  image: Uint8Array,
  profile: KenwoodM60Profile,
  number: number,
): WebChannel {
  if (!active(image, number)) return emptyChannel(number)
  const offset = (number - 1) * 8
  const raw = image.slice(offset, offset + 8)
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency = raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const toneOffset = 0x0100 + (number - 1) * 4
  const txTone = decodeTone(image.slice(toneOffset + 2, toneOffset + 4))
  const rxtone = decodeTone(image.slice(toneOffset, toneOffset + 2))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const flags = image[0x0180 + number - 1]
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: flags & 0x04 ? 'FM' : 'NFM',
    power: flags & 0x08 ? 'High' : 'Low',
    skip: !scan(image, number),
  }
}

export function parseKenwoodM60Channels(
  image: Uint8Array,
  profile: KenwoodM60Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseKenwoodM60Channel(image, profile, index + 1),
  )
}

export function applyKenwoodM60Channel(
  image: Uint8Array,
  profile: KenwoodM60Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > profile.channelMaximum) {
    throw new Error(`Channel must be between 1 and ${profile.channelMaximum}.`)
  }
  setActive(image, channel.number, !channel.empty)
  setScan(image, channel.number, !channel.empty && !channel.skip)
  const offset = (channel.number - 1) * 8
  const raw = image.subarray(offset, offset + 8)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const toneOffset = 0x0100 + (channel.number - 1) * 4
  const existingTx = decodeTone(image.slice(toneOffset + 2, toneOffset + 4))
  const existingRx = decodeTone(image.slice(toneOffset, toneOffset + 2))
  if (!toneMatches(channel, existingTx) || (!toneMatches(channel, existingRx) && existingRx.mode !== 'none')) {
    const tone = encodeTone(channel)
    image.set(tone, toneOffset)
    image.set(tone, toneOffset + 2)
  }
  const flagOffset = 0x0180 + channel.number - 1
  image[flagOffset] =
    (image[flagOffset] & 0xf3) |
    (channel.mode === 'FM' ? 0x04 : 0) |
    (channel.power === 'High' ? 0x08 : 0)
}

function frame(command: number, address: number, data?: Uint8Array): Uint8Array {
  const result = new Uint8Array(data ? 12 : 4)
  result.set([command, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE])
  if (data) result.set(data, 4)
  return result
}

async function handshake(transport: RadioTransport, sendFirst = true): Promise<void> {
  if (sendFirst) await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 2000))[0] !== ACK) {
    throw new Error('The Kenwood radio rejected the block handshake.')
  }
}

async function enterProgrammingMode(
  profile: KenwoodM60Profile,
  transport: RadioTransport,
): Promise<void> {
  for (const value of new TextEncoder().encode('PROGRAM')) {
    await transport.readExact(1, 2500)
    await transport.write(Uint8Array.of(value))
  }
  let found = false
  for (let index = 0; index < 256; index += 1) {
    if ((await transport.readExact(1, 1500))[0] === ACK) {
      found = true
      break
    }
  }
  if (!found) throw new Error('The radio refused Kenwood M60 programming mode.')
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 3000)
  const text = new TextDecoder().decode(identity)
  if (!profile.types.some((type) => text.includes(type))) {
    throw new Error(`The connected radio does not identify as ${profile.model}.`)
  }
  await handshake(transport)
}

async function readBlock(transport: RadioTransport, address: number): Promise<Uint8Array> {
  await transport.write(frame(0x52, address))
  const response = await transport.readExact(12, 3000)
  await handshake(transport)
  return response.slice(4)
}

async function writeBlock(transport: RadioTransport, address: number, data: Uint8Array): Promise<void> {
  await transport.write(frame(0x57, address, data))
  await handshake(transport)
}

export async function readKenwoodM60RadioWithTransport(
  profile: KenwoodM60Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      image.set(await readBlock(transport, index * BLOCK_SIZE), index * BLOCK_SIZE)
      onProgress?.({ current: index + 1, total, label: 'Reading Kenwood M60 memory' })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}

export async function writeKenwoodM60RadioWithTransport(
  profile: KenwoodM60Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) throw new Error('The selected image is smaller than Kenwood M60 memory.')
  await enterProgrammingMode(profile, transport)
  try {
    const total = Math.ceil(0x0380 / BLOCK_SIZE)
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      await writeBlock(transport, address, image.slice(address, address + BLOCK_SIZE))
      onProgress?.({ current: index + 1, total, label: 'Writing Kenwood M60 memory' })
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}
